import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

interface CaseData {
  case_number: number;
  score: number;
  is_relevant: boolean;
  relevance_level: string;
  priority: string;
  pet: string;
  age: string;
  gender: string;
  symptoms: string[];
  diagnosis: string;
  case_number_in_db: string;
  description: string;
  relevancy_reasoning: string;
}

interface AllCasesPanelProps {
  cases: CaseData[];
}

const AllCasesPanel: React.FC<AllCasesPanelProps> = ({ cases }) => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [showPanel, setShowPanel] = useState(true);

  const handleCardExpand = (caseNum: number) => {
    setExpandedCard(expandedCard === caseNum ? null : caseNum);
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.7) return 'success.main';
    if (score >= 0.6) return 'warning.main';
    return 'error.main';
  };

  const getPriorityColor = (priority: string) => {
    const p = priority.toLowerCase();
    if (p === 'emergency') return 'error';
    if (p === 'urgent' || p === 'urgent_gp' || p === 'urgent gp') return 'warning';
    if (p === 'sick') return 'info';
    return 'default';
  };

  const getRelevanceBadge = (isRelevant: boolean, level: string) => {
    if (!isRelevant) {
      return (
        <Chip
          icon={<CancelIcon />}
          label="IRRELEVANT"
          color="default"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      );
    }
    
    if (level === 'HIGHLY_RELEVANT') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="HIGHLY RELEVANT"
          color="success"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      );
    }
    
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="MODERATELY RELEVANT"
        color="warning"
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  if (!cases || cases.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative', mb: 3 }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              📋 All Search Results ({cases.length} cases)
            </Typography>
          </Box>

          <IconButton
            onClick={() => setShowPanel(!showPanel)}
            sx={{ color: 'inherit' }}
            size="small"
          >
            {showPanel ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Box>

        <Collapse in={showPanel}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {cases.map((caseData) => (
                <Card
                  key={caseData.case_number}
                  elevation={2}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      elevation: 4,
                      transform: 'translateY(-2px)',
                    },
                    border: expandedCard === caseData.case_number ? 2 : 0,
                    borderColor: 'primary.main',
                    opacity: caseData.is_relevant ? 1 : 0.7,
                  }}
                  onClick={() => handleCardExpand(caseData.case_number)}
                >
                  <CardContent>
                    {/* Case Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {caseData.case_number_in_db || `Case ${caseData.case_number}`}
                        </Typography>
                        {getRelevanceBadge(caseData.is_relevant, caseData.relevance_level)}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={`Score: ${(caseData.score * 100).toFixed(1)}%`}
                          size="small"
                          sx={{
                            bgcolor: getScoreColor(caseData.score),
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                        <Chip
                          label={caseData.priority.toUpperCase()}
                          color={getPriorityColor(caseData.priority)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Box>

                    {/* Basic Info */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Pet:</strong> {caseData.pet} | <strong>Age:</strong> {caseData.age} | <strong>Gender:</strong> {caseData.gender}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Diagnosis */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        🩺 Diagnosis:
                      </Typography>
                      <Typography variant="body2">
                        {caseData.diagnosis}
                      </Typography>
                    </Box>

                    {/* Symptoms */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        😷 Symptoms:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {caseData.symptoms && caseData.symptoms.length > 0 ? (
                          caseData.symptoms.slice(0, expandedCard === caseData.case_number ? undefined : 5).map((symptom, idx) => (
                            <Chip
                              key={idx}
                              label={symptom}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No symptoms listed
                          </Typography>
                        )}
                        {!expandedCard && caseData.symptoms && caseData.symptoms.length > 5 && (
                          <Chip
                            label={`+${caseData.symptoms.length - 5} more`}
                            size="small"
                            variant="filled"
                            color="primary"
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Expanded Content */}
                    <Collapse in={expandedCard === caseData.case_number}>
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        {/* Relevancy Reasoning */}
                        {caseData.relevancy_reasoning && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: caseData.is_relevant ? 'success.light' : 'grey.100', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              {caseData.is_relevant ? '✅ Why Relevant:' : '❌ Why Not Relevant:'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {caseData.relevancy_reasoning}
                            </Typography>
                          </Box>
                        )}

                        {/* Description */}
                        {caseData.description && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              📝 Case Description:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {caseData.description}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>

                    {/* Expand Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardExpand(caseData.case_number);
                        }}
                        startIcon={
                          <ExpandMoreIcon
                            sx={{
                              transform: expandedCard === caseData.case_number ? 'rotate(180deg)' : 'none',
                              transition: 'transform 0.2s',
                            }}
                          />
                        }
                      >
                        {expandedCard === caseData.case_number ? 'Show Less' : 'Show More'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default AllCasesPanel;

